/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Feature Flags Hooks - React Query for Feature Flags Dashboard
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  featureFlagsService,
  FeatureFlag,
  FlagOverride,
  UpdateFlagRequest,
  CreateOverrideRequest,
} from '@/services/superadmin/feature-flags.service';
import { ApiError } from '@/services/api/types';

export const featureFlagsKeys = {
  all: ['feature-flags'] as const,
  list: (category?: string) => [...featureFlagsKeys.all, 'list', { category }] as const,
  categories: () => [...featureFlagsKeys.all, 'categories'] as const,
  detail: (code: string) => [...featureFlagsKeys.all, 'detail', code] as const,
  overrides: (code: string) => [...featureFlagsKeys.all, 'overrides', code] as const,
};

export function useFeatureFlags(category?: string) {
  return useQuery<FeatureFlag[], ApiError>({
    queryKey: featureFlagsKeys.list(category),
    queryFn: () => featureFlagsService.getAllFlags(category),
    staleTime: 30 * 1000,
  });
}

export function useFlagCategories() {
  return useQuery<string[], ApiError>({
    queryKey: featureFlagsKeys.categories(),
    queryFn: featureFlagsService.getCategories,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFeatureFlag(code: string) {
  return useQuery<FeatureFlag, ApiError>({
    queryKey: featureFlagsKeys.detail(code),
    queryFn: () => featureFlagsService.getFlag(code),
    enabled: !!code,
    staleTime: 30 * 1000,
  });
}

export function useFlagOverrides(code: string) {
  return useQuery<FlagOverride[], ApiError>({
    queryKey: featureFlagsKeys.overrides(code),
    queryFn: () => featureFlagsService.getOverrides(code),
    enabled: !!code,
    staleTime: 30 * 1000,
  });
}

export function useUpdateFlag() {
  const queryClient = useQueryClient();

  return useMutation<FeatureFlag, ApiError, { code: string; request: UpdateFlagRequest }>({
    mutationFn: ({ code, request }) => featureFlagsService.updateFlag(code, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.all });
    },
  });
}

export function useToggleFlag() {
  const queryClient = useQueryClient();

  return useMutation<FeatureFlag, ApiError, string>({
    mutationFn: featureFlagsService.toggleFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.all });
    },
  });
}

export function useCreateOverride() {
  const queryClient = useQueryClient();

  return useMutation<FlagOverride, ApiError, { code: string; request: CreateOverrideRequest }>({
    mutationFn: ({ code, request }) => featureFlagsService.createOverride(code, request),
    onSuccess: (_, { code }) => {
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.overrides(code) });
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.list() });
    },
  });
}

export function useDeleteOverride() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { code: string; orgId: string }>({
    mutationFn: ({ code, orgId }) => featureFlagsService.deleteOverride(code, orgId),
    onSuccess: (_, { code }) => {
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.overrides(code) });
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.list() });
    },
  });
}
